import React, { useState, useEffect, useRef, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import api from './api';
import PioneerBadge from './PioneerBadge';

const getCroppedImg = (imageSrc, pixelCrop) => {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 200;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        200,
        200
      );
      resolve(canvas.toDataURL('image/jpeg', 0.8));
    };
    image.src = imageSrc;
  });
};

const inputStyle = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  background: '#0B0B10',
  border: '1px solid rgba(255, 138, 61, 0.2)',
  borderRadius: '8px',
  color: '#FFFFFF',
  fontSize: '0.9rem',
  outline: 'none',
  boxSizing: 'border-box',
};

const ResultModal = ({ result, pastRaces, onSave, onDelete, onClose }) => {
  const [mode, setMode] = useState(result ? 'manual' : 'manual');
  const [selectedRaceId, setSelectedRaceId] = useState(result?.race_id || '');
  const [raceName, setRaceName] = useState(result?.race_name || '');
  const [raceDate, setRaceDate] = useState(result?.race_date ? result.race_date.split('T')[0] : '');
  const [raceLocation, setRaceLocation] = useState(result?.race_location || '');
  const [distance, setDistance] = useState(result?.distance || '');
  const [distanceUnit, setDistanceUnit] = useState(result?.distance_unit || 'mi');
  const [finishTime, setFinishTime] = useState(result?.finish_time || '');
  const [pace, setPace] = useState(result?.pace || '');
  const [placement, setPlacement] = useState(result?.placement || '');
  const [status, setStatus] = useState(result?.status || 'finished');
  const [notes, setNotes] = useState(result?.notes || '');

  const handleRaceSelect = (raceId) => {
    setSelectedRaceId(raceId);
    if (raceId) {
      const race = pastRaces.find(r => String(r.race_id || r.id) === String(raceId));
      if (race) {
        setRaceName(race.race_name || race.name || '');
        setRaceDate(race.race_date || race.date ? (race.race_date || race.date).split('T')[0] : '');
        setRaceLocation(race.race_location || race.location || '');
      }
    }
  };

  const handleSubmit = () => {
    if (!raceName.trim()) return;
    onSave({
      race_id: selectedRaceId ? Number(selectedRaceId) : null,
      race_name: raceName.trim(),
      race_date: raceDate || null,
      race_location: raceLocation,
      distance,
      distance_unit: distanceUnit,
      finish_time: finishTime,
      pace,
      placement,
      status,
      notes,
    });
  };

  const labelStyle = { display: 'block', fontSize: '0.8rem', color: '#8b9cb3', marginBottom: '0.3rem' };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }}>
      <div style={{
        background: '#0B0B10', borderRadius: '16px',
        border: '1px solid rgba(255, 138, 61, 0.3)',
        padding: '1.5rem', width: '100%', maxWidth: '440px',
        maxHeight: '85vh', overflowY: 'auto',
      }}>
        <h3 style={{ color: '#FFFFFF', fontSize: '1.1rem', fontWeight: 700, margin: '0 0 1.25rem 0', textAlign: 'center' }}>
          {result ? 'Edit Result' : 'Add Race Result'}
        </h3>

        {/* Mode toggle — only for new results */}
        {!result && (
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
            <button onClick={() => setMode('linked')} style={{
              flex: 1, padding: '0.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
              background: mode === 'linked' ? 'rgba(255, 138, 61, 0.15)' : 'transparent',
              border: mode === 'linked' ? '1px solid #FF8A3D' : '1px solid rgba(139, 156, 179, 0.2)',
              color: mode === 'linked' ? '#FF8A3D' : '#8b9cb3',
            }}>From My Races</button>
            <button onClick={() => setMode('manual')} style={{
              flex: 1, padding: '0.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
              background: mode === 'manual' ? 'rgba(255, 138, 61, 0.15)' : 'transparent',
              border: mode === 'manual' ? '1px solid #FF8A3D' : '1px solid rgba(139, 156, 179, 0.2)',
              color: mode === 'manual' ? '#FF8A3D' : '#8b9cb3',
            }}>Manual Entry</button>
          </div>
        )}

        {/* Linked race picker */}
        {mode === 'linked' && !result && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Select Race</label>
            <select
              value={selectedRaceId}
              onChange={(e) => handleRaceSelect(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="">Choose a race...</option>
              {pastRaces.map(r => (
                <option key={r.race_id || r.id} value={r.race_id || r.id}>
                  {r.race_name || r.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Form fields */}
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={labelStyle}>Race Name *</label>
          <input type="text" value={raceName} onChange={e => setRaceName(e.target.value)} placeholder="e.g. NYC Marathon 2025" style={inputStyle} />
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.85rem' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Date</label>
            <input type="date" value={raceDate} onChange={e => setRaceDate(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Location</label>
            <input type="text" value={raceLocation} onChange={e => setRaceLocation(e.target.value)} placeholder="City, State" style={inputStyle} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.85rem' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Distance</label>
            <input type="text" value={distance} onChange={e => setDistance(e.target.value)} placeholder="26.2" style={inputStyle} />
          </div>
          <div style={{ width: '80px' }}>
            <label style={labelStyle}>Unit</label>
            <select value={distanceUnit} onChange={e => setDistanceUnit(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="mi">mi</option>
              <option value="km">km</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '0.85rem' }}>
          <label style={labelStyle}>Status</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {['finished', 'dnf', 'dns'].map(s => (
              <button key={s} onClick={() => setStatus(s)} style={{
                flex: 1, padding: '0.45rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                background: status === s ? (s === 'dnf' ? 'rgba(255,107,107,0.15)' : s === 'dns' ? 'rgba(139,156,179,0.15)' : 'rgba(255,138,61,0.15)') : 'transparent',
                border: status === s ? `1px solid ${s === 'dnf' ? '#ff6b6b' : s === 'dns' ? '#8b9cb3' : '#FF8A3D'}` : '1px solid rgba(139,156,179,0.2)',
                color: status === s ? (s === 'dnf' ? '#ff6b6b' : s === 'dns' ? '#8b9cb3' : '#FF8A3D') : '#8b9cb3',
              }}>{s === 'finished' ? 'Finished' : s.toUpperCase()}</button>
            ))}
          </div>
        </div>

        {status === 'finished' && (
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.85rem' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Finish Time</label>
              <input type="text" value={finishTime} onChange={e => setFinishTime(e.target.value)} placeholder="3:45:12" style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Pace</label>
              <input type="text" value={pace} onChange={e => setPace(e.target.value)} placeholder="8:35/mi" style={inputStyle} />
            </div>
          </div>
        )}

        <div style={{ marginBottom: '0.85rem' }}>
          <label style={labelStyle}>Placement</label>
          <input type="text" value={placement} onChange={e => setPlacement(e.target.value)} placeholder="e.g. 142/5000" style={inputStyle} />
        </div>

        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Personal notes about this race..." rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          {onDelete && (
            <button onClick={onDelete} style={{
              background: 'transparent', border: '1px solid rgba(255, 107, 107, 0.4)', color: '#ff6b6b',
              padding: '0.6rem 1rem', borderRadius: '10px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, marginRight: 'auto',
            }}>Delete</button>
          )}
          <button onClick={onClose} style={{
            background: 'transparent', border: '1px solid rgba(139, 156, 179, 0.3)', color: '#8b9cb3',
            padding: '0.6rem 1.5rem', borderRadius: '10px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600,
          }}>Cancel</button>
          <button onClick={handleSubmit} disabled={!raceName.trim()} style={{
            background: raceName.trim() ? '#FF8A3D' : '#4b5563', border: 'none', color: '#0B0B10',
            padding: '0.6rem 1.5rem', borderRadius: '10px', cursor: raceName.trim() ? 'pointer' : 'not-allowed', fontSize: '0.9rem', fontWeight: 600,
          }}>Save</button>
        </div>
      </div>
    </div>
  );
};

const MyProfile = ({ user, onBack, onProfileUpdate }) => {
  const [firstName, setFirstName] = useState(() => {
    if (user.first_name) return user.first_name;
    if (user.name) return user.name.split(' ')[0] || '';
    return '';
  });
  const [lastName, setLastName] = useState(() => {
    if (user.last_name) return user.last_name;
    if (user.name) return user.name.split(' ').slice(1).join(' ') || '';
    return '';
  });
  const [profilePhoto, setProfilePhoto] = useState(user.profile_photo || '');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const fileInputRef = useRef(null);
  const [myTags, setMyTags] = useState([]);
  const [presetTags, setPresetTags] = useState([]);
  const [customTagInput, setCustomTagInput] = useState('');
  const [tagError, setTagError] = useState('');
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [results, setResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(true);
  const [showAddResult, setShowAddResult] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [pastRaces, setPastRaces] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [sponsorForm, setSponsorForm] = useState({ name: '', logo_url: '', website: '' });
  const [showAddSponsor, setShowAddSponsor] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [editingClub, setEditingClub] = useState(null);
  const [clubForm, setClubForm] = useState({ name: '', role: 'Member' });
  const [showAddClub, setShowAddClub] = useState(false);
  const [clubDirectory, setClubDirectory] = useState([]);
  const [clubSearch, setClubSearch] = useState('');
  const [showClubDropdown, setShowClubDropdown] = useState(false);
  const clubInputRef = useRef(null);

  useEffect(() => {
    api.get('/tags').then(res => setMyTags(res.data)).catch(() => {});
    api.get('/tags/presets').then(res => setPresetTags(res.data)).catch(() => {});
    api.get('/results').then(res => { setResults(res.data); setLoadingResults(false); }).catch(() => setLoadingResults(false));
    api.get('/registrations').then(res => setPastRaces(res.data || [])).catch(() => {});
    api.get('/sponsors').then(res => setSponsors(res.data)).catch(() => {});
    api.get('/clubs').then(res => setClubs(res.data)).catch(() => {});
    api.get('/clubs/directory').then(res => setClubDirectory(res.data)).catch(() => {});
  }, []);

  const handleAddTag = async (tag) => {
    setTagError('');
    try {
      await api.post('/tags', { tag });
      setMyTags(prev => [...prev, tag]);
      return true;
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to add tag';
      setTagError(msg);
      console.error('Add tag error:', err);
      return false;
    }
  };

  const handleAddCustomTag = async () => {
    const tag = customTagInput.trim().toLowerCase();
    if (!tag) return;
    if (tag.length < 2 || tag.length > 30) {
      setTagError('Tag must be between 2 and 30 characters');
      return;
    }
    if (!/^[a-zA-Z0-9 -]+$/.test(tag)) {
      setTagError('Tag can only contain letters, numbers, hyphens, and spaces');
      return;
    }
    if (myTags.includes(tag)) {
      setTagError('You already have this tag');
      return;
    }
    const success = await handleAddTag(tag);
    if (success) setCustomTagInput('');
  };

  const handleRemoveTag = async (tag) => {
    try {
      await api.delete(`/tags/${encodeURIComponent(tag)}`);
      setMyTags(prev => prev.filter(t => t !== tag));
    } catch (err) {
      console.error('Remove tag error:', err);
    }
  };

  const initials = ((firstName || user.name || '')[0] || '') + (lastName[0] || '');

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setCropImage(event.target.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleCropSave = async () => {
    if (!cropImage || !croppedAreaPixels) return;
    const cropped = await getCroppedImg(cropImage, croppedAreaPixels);
    setProfilePhoto(cropped);
    setCropImage(null);
  };

  const handleCropCancel = () => {
    setCropImage(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage('');
    try {
      await onProfileUpdate({
        first_name: firstName,
        last_name: lastName,
        profile_photo: profilePhoto,
      });
      setSaveMessage('Profile saved!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      setSaveMessage('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAddResult = async (data) => {
    try {
      const res = await api.post('/results', data);
      setResults(prev => [res.data, ...prev].sort((a, b) => {
        if (!a.race_date && !b.race_date) return 0;
        if (!a.race_date) return 1;
        if (!b.race_date) return -1;
        return new Date(b.race_date) - new Date(a.race_date);
      }));
      setShowAddResult(false);
    } catch (err) {
      console.error('Add result error:', err);
    }
  };

  const handleUpdateResult = async (id, data) => {
    try {
      const res = await api.put(`/results/${id}`, data);
      setResults(prev => prev.map(r => r.id === id ? res.data : r).sort((a, b) => {
        if (!a.race_date && !b.race_date) return 0;
        if (!a.race_date) return 1;
        if (!b.race_date) return -1;
        return new Date(b.race_date) - new Date(a.race_date);
      }));
      setEditingResult(null);
    } catch (err) {
      console.error('Update result error:', err);
    }
  };

  const handleDeleteResult = async (id) => {
    try {
      await api.delete(`/results/${id}`);
      setResults(prev => prev.filter(r => r.id !== id));
      setEditingResult(null);
    } catch (err) {
      console.error('Delete result error:', err);
    }
  };

  const handleAddSponsor = async () => {
    if (!sponsorForm.name.trim()) return;
    try {
      const res = await api.post('/sponsors', sponsorForm);
      setSponsors(prev => [res.data, ...prev]);
      setSponsorForm({ name: '', logo_url: '', website: '' });
      setShowAddSponsor(false);
    } catch (err) {
      console.error('Add sponsor error:', err);
      setSaveMessage('Failed to save sponsor');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleUpdateSponsor = async (id) => {
    if (!sponsorForm.name.trim()) return;
    try {
      const res = await api.put(`/sponsors/${id}`, sponsorForm);
      setSponsors(prev => prev.map(s => s.id === id ? res.data : s));
      setEditingSponsor(null);
      setSponsorForm({ name: '', logo_url: '', website: '' });
    } catch (err) {
      console.error('Update sponsor error:', err);
      setSaveMessage('Failed to update sponsor');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleDeleteSponsor = async (id) => {
    try {
      await api.delete(`/sponsors/${id}`);
      setSponsors(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Delete sponsor error:', err);
      setSaveMessage('Failed to delete sponsor');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleAddClub = async () => {
    if (!clubForm.name.trim()) return;
    try {
      const res = await api.post('/clubs', clubForm);
      setClubs(prev => [res.data, ...prev]);
      setClubForm({ name: '', role: 'Member' });
      setClubSearch('');
      setShowAddClub(false);
    } catch (err) {
      console.error('Add club error:', err);
      setSaveMessage('Failed to save club');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleSelectClubFromDirectory = (clubName) => {
    setClubForm(f => ({ ...f, name: clubName }));
    setClubSearch(clubName);
    setShowClubDropdown(false);
  };

  const handleAddNewClubToDirectory = async (name) => {
    try {
      const res = await api.post('/clubs/directory', { name, city: '' });
      setClubDirectory(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      handleSelectClubFromDirectory(name);
    } catch (err) { console.error('Add to directory error:', err); }
  };

  const filteredClubDirectory = clubSearch.trim()
    ? clubDirectory.filter(c => c.name.toLowerCase().includes(clubSearch.toLowerCase()))
    : clubDirectory;

  const exactClubMatch = clubSearch.trim() && clubDirectory.some(
    c => c.name.toLowerCase() === clubSearch.trim().toLowerCase()
  );

  const handleUpdateClub = async (id) => {
    if (!clubForm.name.trim()) return;
    try {
      const res = await api.put(`/clubs/${id}`, clubForm);
      setClubs(prev => prev.map(c => c.id === id ? res.data : c));
      setEditingClub(null);
      setClubForm({ name: '', role: 'Member' });
    } catch (err) {
      console.error('Update club error:', err);
      setSaveMessage('Failed to update club');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleDeleteClub = async (id) => {
    try {
      await api.delete(`/clubs/${id}`);
      setClubs(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error('Delete club error:', err);
      setSaveMessage('Failed to delete club');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0B0B10',
      color: '#FFFFFF',
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0B0B10 0%, #16161D 100%)',
        borderBottom: '1px solid rgba(255, 138, 61, 0.2)',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#FF8A3D',
            fontSize: '1.5rem',
            cursor: 'pointer',
            padding: '0.25rem',
          }}
        >
          ←
        </button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>My Profile</h1>
      </div>

      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '2rem 1.5rem',
      }}>
        {/* Profile Photo */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '2rem',
        }}>
          <div
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: '3px solid #FF8A3D',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: profilePhoto ? 'transparent' : '#16161D',
              fontSize: '2.5rem',
              fontWeight: 700,
              color: '#FF8A3D',
            }}
          >
            {profilePhoto ? (
              <img
                src={profilePhoto}
                alt="Profile"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              initials.toUpperCase()
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => fileInputRef.current.click()}
            style={{
              background: 'transparent',
              border: '1px solid #FF8A3D',
              color: '#FF8A3D',
              padding: '0.5rem 1.25rem',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}
          >
            Change Photo
          </button>
          {user.is_pioneer && (
            <div style={{ marginTop: '0.75rem' }}>
              <PioneerBadge isPioneer={user.is_pioneer} pioneerNumber={user.pioneer_number} isCommunityBuilder={user.is_community_builder} />
            </div>
          )}
        </div>

        {/* Name Fields */}
        <div style={{
          background: '#16161D',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          border: '1px solid rgba(255, 138, 61, 0.1)',
        }}>
          <h3 style={{ fontSize: '0.85rem', color: '#8b9cb3', marginBottom: '1rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</h3>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#8b9cb3', marginBottom: '0.4rem' }}>First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: '#0B0B10',
                  border: '1px solid rgba(255, 138, 61, 0.2)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#8b9cb3', marginBottom: '0.4rem' }}>Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: '#0B0B10',
                  border: '1px solid rgba(255, 138, 61, 0.2)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '0.75rem 2rem',
              background: saving ? '#8b9cb3' : '#FF8A3D',
              border: 'none',
              color: '#0B0B10',
              borderRadius: '10px',
              cursor: saving ? 'default' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: 600,
            }}
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
          {saveMessage && (
            <span style={{ fontSize: '0.9rem', color: saveMessage.includes('Failed') ? '#ff6b6b' : '#4ade80' }}>
              {saveMessage}
            </span>
          )}
        </div>

        {/* Profile Tags */}
        <div style={{
          background: '#16161D',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          border: '1px solid rgba(255, 138, 61, 0.1)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '0.85rem', color: '#8b9cb3', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
              Tags
            </h3>
            <span style={{ fontSize: '0.75rem', color: '#8b9cb3' }}>
              {myTags.length}/5 max
            </span>
          </div>

          {/* Current tags */}
          {myTags.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {myTags.map(tag => (
                <span key={tag} style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  background: 'rgba(255, 138, 61, 0.15)',
                  color: '#FF8A3D',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '16px',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                }}>
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#FF8A3D',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: '1rem',
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Available presets */}
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {presetTags
              .filter(tag => !myTags.includes(tag))
              .map(tag => (
                <button
                  key={tag}
                  onClick={() => handleAddTag(tag)}
                  disabled={myTags.length >= 5}
                  style={{
                    background: 'rgba(139, 156, 179, 0.1)',
                    border: '1px solid rgba(139, 156, 179, 0.2)',
                    color: myTags.length >= 5 ? '#4b5563' : '#8b9cb3',
                    padding: '0.3rem 0.7rem',
                    borderRadius: '14px',
                    fontSize: '0.8rem',
                    cursor: myTags.length >= 5 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  + {tag}
                </button>
              ))}
          </div>

          {/* Custom tag input */}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <input
              type="text"
              value={customTagInput}
              onChange={e => { setCustomTagInput(e.target.value); setTagError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomTag(); } }}
              placeholder="Create custom tag..."
              disabled={myTags.length >= 5}
              style={{
                ...inputStyle,
                flex: 1,
              }}
            />
            <button
              onClick={handleAddCustomTag}
              disabled={myTags.length >= 5 || !customTagInput.trim()}
              style={{
                background: 'rgba(255, 138, 61, 0.15)',
                border: '1px solid rgba(255, 138, 61, 0.3)',
                color: myTags.length >= 5 || !customTagInput.trim() ? '#4b5563' : '#FF8A3D',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: myTags.length >= 5 || !customTagInput.trim() ? 'not-allowed' : 'pointer',
              }}
            >
              Add
            </button>
          </div>
          {tagError && (
            <div style={{ color: '#ff6b6b', fontSize: '0.8rem', marginTop: '0.5rem' }}>
              {tagError}
            </div>
          )}
        </div>

        {/* Verified Athlete Badge */}
        <div style={{
          background: '#16161D',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          border: '1px solid rgba(255, 138, 61, 0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: user.verified_athlete ? 'rgba(255, 138, 61, 0.2)' : 'rgba(139, 156, 179, 0.1)',
            fontSize: '1.2rem',
          }}>
            {user.verified_athlete ? '✓' : '○'}
          </div>
          <div>
            <div style={{
              fontWeight: 600,
              color: user.verified_athlete ? '#FF8A3D' : '#8b9cb3',
              marginBottom: '0.25rem',
            }}>
              {user.verified_athlete ? 'Verified Athlete' : 'Not Verified'}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#8b9cb3' }}>
              {user.verified_athlete
                ? 'Your athlete status has been verified via Strava.'
                : 'Strava connection coming soon to verify your athlete status.'}
            </div>
          </div>
        </div>

        {/* Race Results */}
        <div style={{
          background: '#16161D',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          border: '1px solid rgba(255, 138, 61, 0.1)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '0.85rem', color: '#8b9cb3', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
              Race Results
            </h3>
            <button
              onClick={() => setShowAddResult(true)}
              style={{
                background: 'transparent',
                border: '1px solid #FF8A3D',
                color: '#FF8A3D',
                padding: '0.25rem 0.75rem',
                borderRadius: '14px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 600,
              }}
            >
              + Add Result
            </button>
          </div>
          {loadingResults ? (
            <p style={{ color: '#8b9cb3', fontSize: '0.85rem', margin: 0 }}>Loading...</p>
          ) : results.length === 0 ? (
            <p style={{ color: '#8b9cb3', fontSize: '0.85rem', margin: 0 }}>No race results yet. Add your first one!</p>
          ) : (
            results.map((r, i) => (
              <div
                key={r.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem 0',
                  borderTop: i > 0 ? '1px solid rgba(139, 156, 179, 0.1)' : 'none',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>{r.race_name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#8b9cb3' }}>
                    {r.race_date ? new Date(r.race_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                    {r.distance ? ` · ${r.distance} ${r.distance_unit}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {r.status === 'dnf' ? (
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#ff6b6b', background: 'rgba(255, 107, 107, 0.15)', padding: '0.15rem 0.5rem', borderRadius: '8px' }}>DNF</span>
                  ) : r.status === 'dns' ? (
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#8b9cb3', background: 'rgba(139, 156, 179, 0.15)', padding: '0.15rem 0.5rem', borderRadius: '8px' }}>DNS</span>
                  ) : r.finish_time ? (
                    <span style={{ fontFamily: 'monospace', fontSize: '0.95rem', color: '#FF8A3D', fontWeight: 600 }}>{r.finish_time}</span>
                  ) : null}
                  <button
                    onClick={() => setEditingResult(r)}
                    style={{ background: 'transparent', border: 'none', color: '#8b9cb3', cursor: 'pointer', fontSize: '0.85rem', padding: '0.2rem' }}
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Sponsors */}
        <div style={{
          background: '#16161D',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          border: '1px solid rgba(255, 138, 61, 0.1)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '0.85rem', color: '#8b9cb3', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
              Sponsors
            </h3>
            <button
              onClick={() => { setShowAddSponsor(true); setEditingSponsor(null); setSponsorForm({ name: '', logo_url: '', website: '' }); }}
              style={{
                background: 'transparent', border: '1px solid #FF8A3D', color: '#FF8A3D',
                padding: '0.25rem 0.75rem', borderRadius: '14px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
              }}
            >+ Add Sponsor</button>
          </div>

          {(showAddSponsor || editingSponsor) && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#0B0B10', borderRadius: '10px', border: '1px solid rgba(255, 138, 61, 0.15)' }}>
              <input type="text" placeholder="Sponsor name *" value={sponsorForm.name} onChange={e => setSponsorForm(f => ({ ...f, name: e.target.value }))} style={{ ...inputStyle, marginBottom: '0.5rem' }} />
              <input type="text" placeholder="Website URL" value={sponsorForm.website} onChange={e => setSponsorForm(f => ({ ...f, website: e.target.value }))} style={{ ...inputStyle, marginBottom: '0.5rem' }} />
              <input type="text" placeholder="Logo URL" value={sponsorForm.logo_url} onChange={e => setSponsorForm(f => ({ ...f, logo_url: e.target.value }))} style={{ ...inputStyle, marginBottom: '0.75rem' }} />
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={() => { setShowAddSponsor(false); setEditingSponsor(null); }} style={{ background: 'transparent', border: '1px solid rgba(139,156,179,0.3)', color: '#8b9cb3', padding: '0.4rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Cancel</button>
                <button onClick={() => editingSponsor ? handleUpdateSponsor(editingSponsor.id) : handleAddSponsor()} disabled={!sponsorForm.name.trim()} style={{ background: sponsorForm.name.trim() ? '#FF8A3D' : '#4b5563', border: 'none', color: '#0B0B10', padding: '0.4rem 1rem', borderRadius: '8px', cursor: sponsorForm.name.trim() ? 'pointer' : 'not-allowed', fontSize: '0.8rem', fontWeight: 600 }}>Save</button>
              </div>
            </div>
          )}

          {sponsors.length === 0 && !showAddSponsor ? (
            <p style={{ color: '#8b9cb3', fontSize: '0.85rem', margin: 0 }}>No sponsors yet.</p>
          ) : (
            sponsors.map((s, i) => (
              <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderTop: i > 0 ? '1px solid rgba(139,156,179,0.1)' : 'none' }}>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>{s.name}</div>
                  {s.website && <a href={s.website.startsWith('http') ? s.website : `https://${s.website}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8rem', color: '#FF8A3D', textDecoration: 'none' }}>{s.website}</a>}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => { setEditingSponsor(s); setShowAddSponsor(false); setSponsorForm({ name: s.name, logo_url: s.logo_url || '', website: s.website || '' }); }} style={{ background: 'transparent', border: 'none', color: '#8b9cb3', cursor: 'pointer', fontSize: '0.85rem' }}>Edit</button>
                  <button onClick={() => handleDeleteSponsor(s.id)} style={{ background: 'transparent', border: 'none', color: '#ff6b6b', cursor: 'pointer', fontSize: '0.85rem' }}>Delete</button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Running Clubs */}
        <div style={{
          background: '#16161D',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          border: '1px solid rgba(255, 138, 61, 0.1)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '0.85rem', color: '#8b9cb3', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
              Running Clubs
            </h3>
            <button
              onClick={() => { setShowAddClub(true); setEditingClub(null); setClubForm({ name: '', role: 'Member' }); }}
              style={{
                background: 'transparent', border: '1px solid #FF8A3D', color: '#FF8A3D',
                padding: '0.25rem 0.75rem', borderRadius: '14px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
              }}
            >+ Add Club</button>
          </div>

          {(showAddClub || editingClub) && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#0B0B10', borderRadius: '10px', border: '1px solid rgba(255, 138, 61, 0.15)' }}>
              <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
                <input
                  ref={clubInputRef}
                  type="text"
                  placeholder="Search or type club name..."
                  value={showClubDropdown ? clubSearch : clubForm.name}
                  onChange={e => {
                    const val = e.target.value;
                    setClubSearch(val);
                    setClubForm(f => ({ ...f, name: val }));
                    setShowClubDropdown(true);
                  }}
                  onFocus={() => {
                    setClubSearch(clubForm.name);
                    setShowClubDropdown(true);
                  }}
                  onBlur={() => {
                    // Delay to allow click on dropdown items
                    setTimeout(() => setShowClubDropdown(false), 200);
                  }}
                  style={inputStyle}
                />
                {showClubDropdown && (filteredClubDirectory.length > 0 || (clubSearch.trim() && !exactClubMatch)) && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: '#16161D', border: '1px solid rgba(255, 138, 61, 0.2)',
                    borderRadius: '0 0 8px 8px', maxHeight: '180px', overflowY: 'auto',
                  }}>
                    {filteredClubDirectory.slice(0, 8).map(c => (
                      <div
                        key={c.id}
                        onMouseDown={() => handleSelectClubFromDirectory(c.name)}
                        style={{
                          padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem',
                          color: '#FFFFFF', borderBottom: '1px solid rgba(139,156,179,0.08)',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,138,61,0.1)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        {c.name}
                        {c.city ? <span style={{ color: '#8b9cb3', fontSize: '0.75rem', marginLeft: '0.5rem' }}>{c.city}</span> : null}
                      </div>
                    ))}
                    {clubSearch.trim() && !exactClubMatch && (
                      <div
                        onMouseDown={() => handleAddNewClubToDirectory(clubSearch.trim())}
                        style={{
                          padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem',
                          color: '#FF8A3D', borderTop: filteredClubDirectory.length > 0 ? '1px solid rgba(255,138,61,0.15)' : 'none',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,138,61,0.1)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        + Add "{clubSearch.trim()}" as new club
                      </div>
                    )}
                  </div>
                )}
              </div>
              <select value={clubForm.role} onChange={e => setClubForm(f => ({ ...f, role: e.target.value }))} style={{ ...inputStyle, marginBottom: '0.75rem', cursor: 'pointer' }}>
                <option value="Member">Member</option>
                <option value="Board Member">Board Member</option>
                <option value="President">President</option>
                <option value="Coach">Coach</option>
              </select>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={() => { setShowAddClub(false); setEditingClub(null); setClubSearch(''); }} style={{ background: 'transparent', border: '1px solid rgba(139,156,179,0.3)', color: '#8b9cb3', padding: '0.4rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Cancel</button>
                <button onClick={() => editingClub ? handleUpdateClub(editingClub.id) : handleAddClub()} disabled={!clubForm.name.trim()} style={{ background: clubForm.name.trim() ? '#FF8A3D' : '#4b5563', border: 'none', color: '#0B0B10', padding: '0.4rem 1rem', borderRadius: '8px', cursor: clubForm.name.trim() ? 'pointer' : 'not-allowed', fontSize: '0.8rem', fontWeight: 600 }}>Save</button>
              </div>
            </div>
          )}

          {clubs.length === 0 && !showAddClub ? (
            <p style={{ color: '#8b9cb3', fontSize: '0.85rem', margin: 0 }}>No running clubs yet.</p>
          ) : (
            clubs.map((c, i) => (
              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderTop: i > 0 ? '1px solid rgba(139,156,179,0.1)' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  <span style={{ fontSize: '0.75rem', color: '#FF8A3D', background: 'rgba(255,138,61,0.12)', padding: '0.15rem 0.5rem', borderRadius: '10px', fontWeight: 500 }}>{c.role}</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => { setEditingClub(c); setShowAddClub(false); setClubForm({ name: c.name, role: c.role || 'Member' }); setClubSearch(c.name); }} style={{ background: 'transparent', border: 'none', color: '#8b9cb3', cursor: 'pointer', fontSize: '0.85rem' }}>Edit</button>
                  <button onClick={() => handleDeleteClub(c.id)} style={{ background: 'transparent', border: 'none', color: '#ff6b6b', cursor: 'pointer', fontSize: '0.85rem' }}>Delete</button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Account Info */}
        <div style={{
          background: '#16161D',
          borderRadius: '16px',
          padding: '1.5rem',
          border: '1px solid rgba(255, 138, 61, 0.1)',
        }}>
          <h3 style={{ fontSize: '0.85rem', color: '#8b9cb3', marginBottom: '1rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Account</h3>
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '0.8rem', color: '#8b9cb3', marginBottom: '0.2rem' }}>Email</div>
            <div>{user.email}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: '#8b9cb3', marginBottom: '0.2rem' }}>Member Since</div>
            <div>{user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A'}</div>
          </div>
        </div>
      </div>

      {/* Add/Edit Result Modal */}
      {(showAddResult || editingResult) && (
        <ResultModal
          result={editingResult}
          pastRaces={pastRaces}
          onSave={(data) => editingResult ? handleUpdateResult(editingResult.id, data) : handleAddResult(data)}
          onDelete={editingResult ? () => handleDeleteResult(editingResult.id) : null}
          onClose={() => { setShowAddResult(false); setEditingResult(null); }}
        />
      )}

      {/* Crop Modal */}
      {cropImage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#0B0B10',
            borderRadius: '16px',
            border: '1px solid rgba(255, 138, 61, 0.3)',
            padding: '1.5rem',
            width: '90%',
            maxWidth: '400px',
          }}>
            <h3 style={{
              color: '#FFFFFF',
              fontSize: '1.1rem',
              fontWeight: 700,
              margin: '0 0 1rem 0',
              textAlign: 'center',
            }}>
              Crop Photo
            </h3>
            <div style={{
              position: 'relative',
              width: '100%',
              height: '300px',
              borderRadius: '8px',
              overflow: 'hidden',
              marginBottom: '1rem',
            }}>
              <Cropper
                image={cropImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.25rem',
              padding: '0 0.5rem',
            }}>
              <span style={{ fontSize: '0.8rem', color: '#8b9cb3' }}>Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                style={{
                  flex: 1,
                  accentColor: '#FF8A3D',
                }}
              />
            </div>
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={handleCropCancel}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(139, 156, 179, 0.3)',
                  color: '#8b9cb3',
                  padding: '0.6rem 1.5rem',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCropSave}
                style={{
                  background: '#FF8A3D',
                  border: 'none',
                  color: '#0B0B10',
                  padding: '0.6rem 1.5rem',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProfile;
